import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationsEN from 'translations/en.json';
import translationsDA from 'translations/da.json';
import translationsDE from 'translations/de.json';
import translationsPL from 'translations/pl.json';

export const resources = {
  en: { translations: translationsEN },
  da: { translations: translationsDA },
  de: { translations: translationsDE },
  pl: { translations: translationsPL },
};

export const SUPPORTED_LANGUAGES = ['en', 'da', 'de', 'pl'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LANGUAGE_COOKIE_NAME = 'app_language';

const getCookie = (name: string): string | undefined => {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? match.split('=')[1] : undefined;
};

export const setLanguageCookie = (language: SupportedLanguage): void => {
  document.cookie = `${LANGUAGE_COOKIE_NAME}=${language}; path=/; max-age=31536000; SameSite=Lax`;
  i18n.changeLanguage(language);
};

const getBrowserLanguage = (): string => {
  const language = navigator.language;
  switch (language) {
    case 'da-DK':
      return 'da';
    case 'en-US':
    case 'en-GB':
    case 'en-CA':
      return 'en';
    case 'de-AT':
    case 'de-DE':
    case 'de-CH':
      return 'de';
    case 'pl-PL':
      return 'pl';
    default:
      return language;
  }
};

const getLanguage = (): string => {
  const cookieLang = getCookie(LANGUAGE_COOKIE_NAME);
  if (
    cookieLang &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(cookieLang)
  ) {
    return cookieLang;
  }
  return getBrowserLanguage();
};

const getLanguagePart = (language: string) => {
  // ISO 639-1 Browser language "da", "en", "de", "da-DK", "en-GB" etc. to
  // language part e.g. "da", "en", "de"
  return language.split('-')[0];
};

i18n.use(initReactI18next).init({
  lng: getLanguage(),
  fallbackLng: 'en',
  ns: ['translations'],
  resources,
});

export const apiTranslateToLanguage = (
  translations?: Record<string, string>,
  language?: string,
): string => {
  if (!translations) {
    return '';
  }
  const languages = Object.keys(translations);
  if (languages.length === 0) {
    return '';
  }
  if (language) {
    const languagePart = getLanguagePart(language);
    const value = translations[languagePart];
    if (value) {
      return value;
    }
  }
  const value = translations[languages[0]];
  if (value) {
    // some language found
    return value;
  }
  return '';
};
export const apiTranslate = (translations: Record<string, string>): string => {
  const languages = Object.keys(translations);
  if (languages.length === 0) {
    return '';
  }

  const language = getLanguage();
  return apiTranslateToLanguage(translations, language);
};
